import React, { TextareaHTMLAttributes, useCallback, useEffect, useRef } from 'react';
import min from 'lodash/min';
import c from 'classnames';
import InputFieldOutline from '../InputFieldOutline';
import { getExtraHeightFromPaddingAndBorder, getLineHeightInPixels } from './measuring';
import s from './GrowingTextArea.scss';

export interface Props extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'rows'> {
  // We should probably think up a way to pull the line-height of the textarea without hard-coding line-height or relying
  // on a ref that's only available after the initial render.

  /**
   * How many rows the text area will grow to. Row height is determined by the font's line-height.
   */
  maxRows: number;

  /**
   * How many rows the text area should start at and the minimum it can reduce to.
   *
   * Note that this does not add extra text rows, it only manages the height of the textarea. Any click
   * on an empty textarea will have the text cursor appear at the start of the textarea.
   */
  minRows?: number;

  /** Determines whether to show a green or red border around the input. */
  validationResult?: 'success' | 'error';

  className?: string;
}

/**
 * A text area that grows and shrinks with user input.
 */
/*
  Implemented by manually calculating and setting heights based off of the used font's line-height. A <div /> element
  acts as the scroll area, and a <textarea /> acts as the input. As the <textarea /> has content added, we monitor its
  scrollHeight and update its clientHeight to match. This makes an ever expanding <textarea />. The `<div />` expands with
  the `<textarea />`, but only to the configured `maxRows`. That's where your browser's overflow handling takes over.

  There are more tricks to make this thing shrink when content is removed, but you should read the code for that instead
  of this comment.
*/
const GrowingTextArea: React.FC<Props> = ({
  maxRows,
  // If minRows is set to 1, this allows the textarea to shrink to one row without manual intervention
  minRows = 1,
  validationResult,
  className,
  disabled,
  value,
  ...rest
}) => {
  const localTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const inputOutlineRef = useRef<HTMLDivElement>(null);

  const resizeInput = useCallback(
    (newValue: string) => {
      if (inputOutlineRef.current) {
        inputOutlineRef.current.dataset.replicatedValue = newValue;
      }

      // The root element needs a max height to force the textarea inside to scroll. We base this max height on a combination
      // of paddings, margins, borders, the textarea line height, and the user-provided maxRows prop.
      //
      // Since javascript can't easily get the textarea's style values at compile time, we have to fetch them at runtime.
      if (localTextAreaRef.current && inputOutlineRef.current) {
        const textAreaLineHeight = getLineHeightInPixels(localTextAreaRef);
        const originalScrollTop = inputOutlineRef.current.scrollTop;

        const contentHeight = localTextAreaRef.current.scrollHeight;
        const extraHeightFromPaddingAndBorder = getExtraHeightFromPaddingAndBorder(inputOutlineRef);

        // The input outline will grow to either:
        //  1. The text area's scroll height
        //  2. The user-configured max number of rows
        //
        // Which ever is smaller.
        inputOutlineRef.current.style.maxHeight = `${min([
          contentHeight + extraHeightFromPaddingAndBorder,
          textAreaLineHeight * maxRows + extraHeightFromPaddingAndBorder,
        ])}px`;

        // Without manually resetting scrollTop to the original value, the scroll position jumps around when typing.
        inputOutlineRef.current.scrollTop = originalScrollTop;
      }
    },
    [maxRows]
  );

  /**
   * We need to handle controlled and uncontrolled versions of this input. That means we need to:
   * 1. Respond to the `value` prop changing (handled by the `useEffect` below)
   * 2. Native input events (handled by `handleInput` below)
   */
  useEffect(() => {
    resizeInput(value ? `${value}` : '');
  }, [value, resizeInput]);

  const handleInput: React.FormEventHandler<HTMLTextAreaElement> = useCallback(
    e => {
      resizeInput((e.target as HTMLTextAreaElement).value);
    },
    [resizeInput]
  );

  return (
    <InputFieldOutline
      ref={inputOutlineRef}
      className={c(s['root'], className)}
      outlineStyle={disabled ? 'disabled' : validationResult}
      onClick={() => localTextAreaRef.current && localTextAreaRef.current.focus()}
    >
      <textarea
        ref={localTextAreaRef}
        value={value}
        onInput={handleInput}
        className={s['input']}
        rows={minRows}
        {...rest}
      />
    </InputFieldOutline>
  );
};

GrowingTextArea.displayName = 'GrowingTextArea';

export default GrowingTextArea;
